import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { getUiErrorToastText } from '@/shared/api/uiError';
import { toast } from '@/shared/ui';

import {
  useContinuePendingPaymentMutation,
  type ContinuePendingPaymentResult,
} from '../api/ordersApi';

function successDescription(
  t: ReturnType<typeof useTranslation<'orders'>>['t'],
  result: ContinuePendingPaymentResult,
  orderNumber: string,
): string {
  const message = result.message.trim();
  if (message) return message;
  if (result.resolution === 'SWITCHED_TO_COD') {
    return t('continue.toasts.switchedFallback', { orderNumber });
  }
  if (result.resolution === 'ONLINE_PAYMENT_CAPTURED') {
    return t('continue.toasts.capturedFallback', { orderNumber });
  }
  return t('continue.toasts.updatedFallback', { orderNumber });
}

export function useContinuePendingPaymentAction() {
  const { t } = useTranslation('orders');
  const { t: tNav } = useTranslation('common');
  const mutation = useContinuePendingPaymentMutation();
  const { mutateAsync, isPending, variables } = mutation;

  const continueOrder = useCallback(
    async (order: { id: string; orderNumber: string }) => {
      try {
        const result = await mutateAsync({ orderId: order.id });
        const orderNumber = result.orderNumber.trim() || order.orderNumber.trim();
        toast.success({
          title: t('continue.toasts.title', { orderNumber: orderNumber || order.id }),
          description: successDescription(t, result, orderNumber || order.id),
        });
      } catch (err) {
        toast.error(getUiErrorToastText(tNav, err));
      }
    },
    [mutateAsync, t, tNav],
  );

  return {
    continueOrder,
    continuingOrderId: isPending ? variables?.orderId : undefined,
  };
}
